// Market = country/region the user is browsing in. Independent of UI language.
// Each market has a canonical default UI language and a default currency.
//
// `languages` is the full set of UI languages addressable for this market via
// the URL (`/<market>/<lang>/...`). It MUST stay in lock-step with the
// server-side `BAC_MARKETS` map in
// `artifacts/api-server/src/lib/locale-url.ts` — both pathToLocale (server)
// and parseLocaleFromPath (client) use it to decide what `/ca/fr` means.
import { ACTIVE_MARKET_COUNTRIES, countryCodeToMarketLocale } from "@workspace/countries";

export interface Market {
  code: string;          // url segment, e.g. "uk", "de", "global"
  label: string;         // English name
  nativeLabel: string;   // localised display name
  flag: string;          // emoji
  defaultLanguage: string;
  defaultCurrency: string;
  /** Languages addressable for this market in the URL (default-language first). */
  languages: string[];
  /** Public catalogue restoration does not enable new host/payment onboarding. */
  publicOnly?: boolean;
}

export const MARKETS: Market[] = [
  { code: "uk",     label: "United Kingdom",     nativeLabel: "United Kingdom",  flag: "🇬🇧", defaultLanguage: "en", defaultCurrency: "GBP", languages: ["en"] },
  { code: "ie",     label: "Ireland",            nativeLabel: "Ireland",         flag: "🇮🇪", defaultLanguage: "en", defaultCurrency: "EUR", languages: ["en"] },
  { code: "us",     label: "United States",      nativeLabel: "United States",   flag: "🇺🇸", defaultLanguage: "en", defaultCurrency: "USD", languages: ["en"] },
  { code: "ca",     label: "Canada",             nativeLabel: "Canada",          flag: "🇨🇦", defaultLanguage: "en", defaultCurrency: "CAD", languages: ["en", "fr"] },
  { code: "au",     label: "Australia",          nativeLabel: "Australia",       flag: "🇦🇺", defaultLanguage: "en", defaultCurrency: "AUD", languages: ["en"] },
  { code: "nz",     label: "New Zealand",        nativeLabel: "New Zealand",     flag: "🇳🇿", defaultLanguage: "en", defaultCurrency: "NZD", languages: ["en"] },
  { code: "za",     label: "South Africa",       nativeLabel: "South Africa",    flag: "🇿🇦", defaultLanguage: "en", defaultCurrency: "ZAR", languages: ["en"] },
  { code: "de",     label: "Germany",            nativeLabel: "Deutschland",     flag: "🇩🇪", defaultLanguage: "de", defaultCurrency: "EUR", languages: ["de", "en"] },
  { code: "fr",     label: "France",             nativeLabel: "France",          flag: "🇫🇷", defaultLanguage: "fr", defaultCurrency: "EUR", languages: ["fr", "en"] },
  { code: "es",     label: "Spain",              nativeLabel: "España",          flag: "🇪🇸", defaultLanguage: "es", defaultCurrency: "EUR", languages: ["es", "en"] },
  { code: "it",     label: "Italy",              nativeLabel: "Italia",          flag: "🇮🇹", defaultLanguage: "it", defaultCurrency: "EUR", languages: ["it", "en"] },
  { code: "pt",     label: "Portugal",           nativeLabel: "Portugal",        flag: "🇵🇹", defaultLanguage: "pt", defaultCurrency: "EUR", languages: ["pt", "en"] },
  { code: "dk",     label: "Denmark",            nativeLabel: "Danmark",         flag: "🇩🇰", defaultLanguage: "da", defaultCurrency: "DKK", languages: ["da", "en"] },
  { code: "se",     label: "Sweden",             nativeLabel: "Sverige",         flag: "🇸🇪", defaultLanguage: "sv", defaultCurrency: "SEK", languages: ["sv", "en"] },
  { code: "mt",     label: "Malta",              nativeLabel: "Malta",           flag: "🇲🇹", defaultLanguage: "en", defaultCurrency: "EUR", languages: ["en"] },
  { code: "br",     label: "Brazil",             nativeLabel: "Brasil",          flag: "🇧🇷", defaultLanguage: "pt-BR", defaultCurrency: "BRL", languages: ["pt-BR", "en"] },
  { code: "co",     label: "Colombia",           nativeLabel: "Colombia",        flag: "🇨🇴", defaultLanguage: "es", defaultCurrency: "COP", languages: ["es", "en"] },
  { code: "ec",     label: "Ecuador",            nativeLabel: "Ecuador",         flag: "🇪🇨", defaultLanguage: "es", defaultCurrency: "USD", languages: ["es", "en"] },
  { code: "mx",     label: "Mexico",             nativeLabel: "México",          flag: "🇲🇽", defaultLanguage: "es", defaultCurrency: "MXN", languages: ["es", "en"] },
  { code: "be",     label: "Belgium",            nativeLabel: "België / Belgique", flag: "🇧🇪", defaultLanguage: "nl", defaultCurrency: "EUR", languages: ["nl", "fr", "en"] },
  { code: "pl",     label: "Poland",             nativeLabel: "Polska",           flag: "🇵🇱", defaultLanguage: "pl", defaultCurrency: "PLN", languages: ["pl", "en"] },
  { code: "sk",     label: "Slovakia",           nativeLabel: "Slovensko",        flag: "🇸🇰", defaultLanguage: "sk", defaultCurrency: "EUR", languages: ["sk", "en"] },
  { code: "no",     label: "Norway",             nativeLabel: "Norge",            flag: "🇳🇴", defaultLanguage: "no", defaultCurrency: "NOK", languages: ["no", "en"] },
  { code: "bg",     label: "Bulgaria",           nativeLabel: "България",         flag: "🇧🇬", defaultLanguage: "bg", defaultCurrency: "EUR", languages: ["bg", "en"] },
  { code: "fi",     label: "Finland",            nativeLabel: "Suomi",            flag: "🇫🇮", defaultLanguage: "fi", defaultCurrency: "EUR", languages: ["fi", "en"] },
  { code: "nl",     label: "Netherlands",        nativeLabel: "Nederland",        flag: "🇳🇱", defaultLanguage: "nl", defaultCurrency: "EUR", languages: ["nl", "en"] },
  { code: "at",     label: "Austria",            nativeLabel: "Österreich",       flag: "🇦🇹", defaultLanguage: "de", defaultCurrency: "EUR", languages: ["de", "en"] },
  { code: "cz",     label: "Czech Republic",     nativeLabel: "Česko",            flag: "🇨🇿", defaultLanguage: "cs", defaultCurrency: "CZK", languages: ["cs", "en"] },
  { code: "in",     label: "India",              nativeLabel: "भारत",              flag: "🇮🇳", defaultLanguage: "en-IN", defaultCurrency: "INR", languages: ["en-IN"] },
  { code: "ch",     label: "Switzerland",        nativeLabel: "Schweiz",          flag: "🇨🇭", defaultLanguage: "de", defaultCurrency: "CHF", languages: ["de", "fr", "it", "en"] },
  { code: "ar",     label: "Argentina",          nativeLabel: "Argentina",        flag: "🇦🇷", defaultLanguage: "es", defaultCurrency: "ARS", languages: ["es", "en"] },
  { code: "lt",     label: "Lithuania",          nativeLabel: "Lithuania",        flag: "🇱🇹", defaultLanguage: "en", defaultCurrency: "EUR", languages: ["en"] },
  { code: "lv",     label: "Latvia",             nativeLabel: "Latvia",           flag: "🇱🇻", defaultLanguage: "en", defaultCurrency: "EUR", languages: ["en"] },
  { code: "global", label: "Worldwide",          nativeLabel: "Worldwide",       flag: "🌍", defaultLanguage: "en", defaultCurrency: "USD", languages: ["en"] },
];

export const MARKETS_BY_CODE: Record<string, Market> = Object.fromEntries(MARKETS.map((m) => [m.code, m]));

/**
 * Task #2444 — market → BCP-47 locale for number / currency
 * formatting. Independent of UI language: a French-speaking visitor on
 * the Canadian market still wants Canadian number conventions, not
 * European-French ones. `Intl.NumberFormat` consumes these directly.
 *
   * Worldwide / unknown markets use US formatting unless UK English was
   * explicitly selected; country routes retain their own conventions.
 */
const MARKET_FORMAT_LOCALES: Record<string, string> = {
  uk: "en-GB",
  ie: "en-IE",
  us: "en-US",
  ca: "en-CA",
  au: "en-AU",
  nz: "en-NZ",
  za: "en-ZA",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
  pt: "pt-PT",
  dk: "da-DK",
  se: "sv-SE",
  mt: "en-MT",
  br: "pt-BR",
  co: "es-CO",
  ec: "es-EC",
  mx: "es-MX",
  be: "nl-BE",
  pl: "pl-PL",
  sk: "sk-SK",
  no: "nb-NO",
  bg: "bg-BG",
  fi: "fi-FI",
  nl: "nl-NL",
  at: "de-AT",
  cz: "cs-CZ",
  in: "en-IN",
  ch: "de-CH",
  ar: "es-AR",
  lt: "en-LT",
  lv: "en-LV",
  global: "en-US",
};

export function marketToFormatLocale(
  market?: string | null,
  language?: string | null,
): string {
  const m = (market || "").toLowerCase();
  const lang = (language || "").toLowerCase();
  // Canada has two addressable UI languages; honour `fr` to get
  // French-Canadian number conventions when the URL pins it.
  if (m === "ca" && lang === "fr") return "fr-CA";
  if (m === "ch" && ["de", "fr", "it", "en"].includes(lang)) return `${lang}-CH`;
  if (m === "be" && lang) {
    const belgianLocales: Record<string, string> = { nl: "nl-BE", fr: "fr-BE", en: "en-BE" };
    return belgianLocales[lang] || MARKET_FORMAT_LOCALES[m];
  }
  if (!m || m === "global" || !MARKET_FORMAT_LOCALES[m]) {
    const worldLanguageLocales: Record<string, string> = {
      fr: "fr-FR", de: "de-DE", es: "es-ES", it: "it-IT", cs: "cs-CZ",
      nl: "nl-NL", da: "da-DK", sv: "sv-SE", no: "nb-NO",
      pl: "pl-PL", sk: "sk-SK", bg: "bg-BG", fi: "fi-FI",
      "pt-br": "pt-BR", "en-in": "en-IN",
    };
    if (lang && worldLanguageLocales[lang]) return worldLanguageLocales[lang];
    return lang === "en-gb" || lang === "en" ? "en-GB" : "en-US";
  }
  return MARKET_FORMAT_LOCALES[m];
}

const COUNTRY_TO_MARKET: Record<string, string> = {
  GB: "uk", IE: "ie", US: "us", CA: "ca", AU: "au", NZ: "nz", ZA: "za",
  DE: "de", FR: "fr", ES: "es", IT: "it", PT: "pt", DK: "dk", SE: "se", MT: "mt",
  BR: "br", CO: "co", EC: "ec", MX: "mx", BE: "be", PL: "pl", SK: "sk",
  NO: "no", BG: "bg", FI: "fi", NL: "nl",
  AT: "at", CZ: "cz", IN: "in", CH: "ch", AR: "ar", LT: "lt", LV: "lv",
};

// Inverse lookup: market code -> ISO-3166 alpha-2 country.
// "global" intentionally has no mapping (returns null).
const MARKET_TO_COUNTRY: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_TO_MARKET).map(([country, market]) => [market, country]),
);

export function countryToMarket(country?: string | null): string | null {
  if (!country) return null;
  return countryCodeToMarketLocale(country)?.market || COUNTRY_TO_MARKET[country.toUpperCase()] || null;
}

// Currencies the app can actually display. Mirror of `SUPPORTED_CURRENCIES`
// in `CurrencyContext.tsx` — kept as a local set here (rather than imported)
// to avoid a circular import, since `CurrencyContext` already imports from
// this module. If you add a supported currency, update both lists.
const SUPPORTED_CURRENCY_CODES = new Set<string>([
  "USD", "EUR", "GBP", "AUD", "CAD", "NZD", "ZAR", "DKK", "SEK", "NOK",
  "CZK", "INR", "BRL", "COP", "MXN", "PLN", "CHF", "ARS",
]);

// ISO-3166 alpha-2 → currency for countries that lack a dedicated market but
// whose local currency the app can still display. The big bucket here is
// eurozone members with no own market — they all resolve to EUR — plus any
// other country whose currency happens to be one we support. Markets that DO
// exist are resolved via their `defaultCurrency` in `countryToCurrency` below;
// this map only fills the gaps.
const COUNTRY_TO_CURRENCY_FALLBACK: Record<string, string> = {
  // Eurozone members without a dedicated BAC market → EUR.
  AT: "EUR", // Austria
  GR: "EUR", // Greece
  HR: "EUR", // Croatia
  CY: "EUR", // Cyprus
  EE: "EUR", // Estonia
  LV: "EUR", // Latvia
  LT: "EUR", // Lithuania
  LU: "EUR", // Luxembourg
  SI: "EUR", // Slovenia
};

/**
 * Resolve a visitor's ISO-3166 alpha-2 country to the currency we should
 * display, for geo-defaulting on first load (task #5203). Prefers a dedicated
 * market's `defaultCurrency` when one exists, otherwise falls back to the
 * eurozone/gap map above. Only ever returns a currency in
 * `SUPPORTED_CURRENCY_CODES`; anything else (e.g. Brazil → BRL, which we don't
 * display) returns null so the caller's USD fallback applies.
 */
export function countryToCurrency(country?: string | null): string | null {
  if (!country) return null;
  const cc = country.toUpperCase();
  const market = COUNTRY_TO_MARKET[cc];
  const fromMarket = market ? MARKETS_BY_CODE[market]?.defaultCurrency : undefined;
  const candidate = fromMarket || COUNTRY_TO_CURRENCY_FALLBACK[cc] || null;
  if (candidate && SUPPORTED_CURRENCY_CODES.has(candidate)) return candidate;
  return null;
}

export function marketToCountry(market?: string | null): string | null {
  if (!market) return null;
  return MARKET_TO_COUNTRY[market.toLowerCase()] || null;
}

// ISO-2 countries whose primary (default) language matches `language`.
// Used to show language-relevant social proof on pages with no market pinned
// (e.g. the bare /list-a-car marketing page in English → English-speaking
// markets only), so reviews read in the visitor's UI language.
export function countriesForLanguage(language?: string | null): string[] {
  if (!language) return [];
  const lang = language.toLowerCase();
  return MARKETS
    .filter((m) => m.code !== "global" && m.defaultLanguage.toLowerCase() === lang)
    .map((m) => marketToCountry(m.code))
    .filter((c): c is string => !!c);
}

// ISO-3166 alpha-2 codes for every market a user can actually book in.
// Used to scope the phone country picker to bookable regions only.
// Public country pages can precede onboarding/payment-market activation.
export const BOOKABLE_COUNTRY_ISOS: readonly string[] =
  Object.values(MARKET_TO_COUNTRY).filter(country => ACTIVE_MARKET_COUNTRIES.includes(country));

export const MARKET_STORAGE_KEY = "bac.market";
export const CURRENCY_STORAGE_KEY = "bac.currency";

export function getStoredMarket(): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(MARKET_STORAGE_KEY); } catch { return null; }
}
export function setStoredMarket(code: string): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(MARKET_STORAGE_KEY, code); } catch { /* noop */ }
  document.cookie = `${MARKET_STORAGE_KEY}=${encodeURIComponent(code)}; path=/; max-age=${60*60*24*365}; SameSite=Lax`;
}
